// /frontend/src/pages/checkout/PaymentPage.jsx - Modified for RFQ
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import CheckoutSteps from './CheckoutSteps';
import Spinner from '../../components/ui/Spinner';
import { toast } from 'react-toastify';
import { orderService } from '../../services/orderService';
import { FaCreditCard, FaMoneyCheck, FaUniversity } from 'react-icons/fa';
import { isRFQModeEnabled, getRFQTexts, getNotificationConfig } from '../../config/rfqConfig';

const PaymentPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector(state => state.auth);
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // RFQ Configuration
  const isRFQMode = isRFQModeEnabled();
  const rfqTexts = getRFQTexts();
  const notificationConfig = getNotificationConfig();

  const fetchOrder = async () => {
    try {
      console.log('Fetching order data for ID:', orderId);
      setLoading(true);
      const data = await orderService.getOrderById(orderId);
      
      console.log('Received order data:', data);
      
      // Check if this is an RFQ order (total price is 0 or RFQ mode is enabled)
      const isRFQOrder = isRFQMode || data?.order?.totalPrice === 0 || data?.totalPrice === 0;
      
      if (isRFQOrder) {
        console.log('Detected RFQ order, redirecting to confirmation page');
        toast.info('This is a quote request. Payment will be required after quote approval.');
        navigate(`/order/confirmation/${orderId}`);
        return;
      }
      
      // Check if already paid (for non-RFQ orders)
      if (data.isPaid && !data.paymentEvidence) {
        toast.info('Payment has already been completed for this order');
        navigate(`/order/confirmation/${orderId}`);
        return;
      }
      
      setOrder(data);
      
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.response?.data?.message || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/payment/' + orderId);
      return;
    }
    
    if (!orderId) {
      navigate('/cart');
      return;
    }
    
    fetchOrder();
  }, [orderId, isAuthenticated, navigate]);

  // If RFQ mode is enabled globally, always redirect
  useEffect(() => {
    if (isRFQMode && rfqTexts.paymentPageRedirect) {
      console.log('Global RFQ mode enabled, redirecting from payment page');
      navigate(`/order/confirmation/${orderId}`);
    }
  }, [isRFQMode, orderId, navigate, rfqTexts.paymentPageRedirect]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <CheckoutSteps step="payment" orderId={orderId} />
          <div className="flex justify-center items-center h-64">
            <Spinner />
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <CheckoutSteps step="payment" orderId={orderId} />
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
            <p>{error}</p>
          </div>
          <Link to={`/order/confirmation/${orderId}`} className="text-teal-600 hover:text-teal-800">
            ← Back to Order Confirmation
          </Link>
        </div>
      </Layout>
    );
  }

  // If we reach this point in RFQ mode, show a message instead of payment form
  if (isRFQMode) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <CheckoutSteps step="payment" orderId={orderId} />
          
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-blue-50 border-b">
              <h2 className="text-xl font-semibold text-blue-800">Quote Request Mode</h2>
            </div>
            <div className="p-6">
              <div className="text-center py-8">
                <svg className="h-16 w-16 mx-auto text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <h3 className="text-xl font-medium mb-4 text-blue-800">Payment Not Required Yet</h3>
                <p className="text-blue-700 mb-6 max-w-md mx-auto">
                  This is a quote request. Payment will only be required after you receive and approve your custom quotation.
                </p>
                
                <div className="bg-blue-50 p-4 rounded-lg mb-6 max-w-md mx-auto">
                  <h4 className="font-medium text-blue-800 mb-2">What happens next?</h4>
                  <ol className="text-sm text-blue-700 text-left list-decimal list-inside space-y-1">
                    <li>Our team reviews your request within {notificationConfig.estimatedResponseTime}</li>
                    <li>You'll receive a detailed quote via email</li>
                    <li>Review and approve the quote</li>
                    <li>Payment instructions will be provided upon approval</li>
                  </ol>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to={`/order/confirmation/${orderId}`}
                    className="px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    View Quote Request
                  </Link>
                  
                  <Link 
                    to="/account/orders"
                    className="px-6 py-3 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  >
                    My Quote Requests
                  </Link>
                </div>
                
                <p className="text-sm text-gray-500 mt-6">
                  Questions? Contact us at <strong>{notificationConfig.contactEmail}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // If not in RFQ mode but order doesn't exist, show error
  if (!order) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <CheckoutSteps step="payment" orderId={orderId} />
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
            <p>Order not found</p>
          </div>
          <Link to="/account/orders" className="text-teal-600 hover:text-teal-800">
            ← Back to My Orders
          </Link>
        </div>
      </Layout>
    );
  }

  // Normal payment page content for non-RFQ orders
  // (Keep all the existing payment functionality here...)
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <CheckoutSteps step="payment" orderId={orderId} />
        
        <div className="flex justify-between items-center mb-8">
          <div>
            <Link to={`/order/confirmation/${orderId}`} className="text-teal-600 hover:text-teal-800 mb-2 inline-block">
              ← Back to Order Confirmation
            </Link>
            <h1 className="text-3xl font-bold">Complete Payment</h1>
          </div>
        </div>

        {/* Payment form components would go here for non-RFQ orders */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-xl font-semibold">Payment Required</h2>
          </div>
          <div className="p-6">
            <div className="text-center py-8">
              <svg className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <h3 className="text-xl font-medium mb-2">Payment Processing</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                This order requires payment processing. Payment functionality is currently being implemented.
              </p>
              <div className="flex justify-center">
                <Link
                  to={`/order/confirmation/${orderId}`}
                  className="px-6 py-2 bg-teal-800 text-white rounded-md hover:bg-teal-900 transition-colors"
                >
                  Back to Order Details
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentPage;